import { useState, useEffect, useRef, useCallback } from 'react'

interface UseIntersectionObserverOptions {
  threshold?: number | number[]
  rootMargin?: string
  once?: boolean
}

interface UseIntersectionObserverResult {
  ref: (node: Element | null) => void
  isVisible: boolean
}

export function useIntersectionObserver(
  options: UseIntersectionObserverOptions = {}
): UseIntersectionObserverResult {
  const { threshold = 0.1, rootMargin = '0px', once = false } = options

  // Always start false — consistent between server and client, no hydration mismatch
  const [isVisible, setIsVisible] = useState(false)

  const observerRef = useRef<IntersectionObserver | null>(null)
  const elementRef = useRef<Element | null>(null)
  const hasBeenVisible = useRef(false)

  const disconnect = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
  }, [])

  const ref = useCallback(
    (node: Element | null) => {
      if (!('IntersectionObserver' in window)) return

      disconnect()
      elementRef.current = node

      if (!node) return

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (entry.isIntersecting) {
            setIsVisible(true)
            hasBeenVisible.current = true
            if (once) {
              disconnect()
            }
          } else {
            if (!once) {
              setIsVisible(false)
            }
          }
        },
        { threshold, rootMargin }
      )

      observerRef.current.observe(node)
    },
    [threshold, rootMargin, once, disconnect]
  )

  useEffect(() => {
    // Fallback for browsers without IntersectionObserver — runs client-only
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true)
    }
    return () => {
      disconnect()
    }
  }, [disconnect])

  return { ref, isVisible }
}
